/**
 * useNovaDataRefresh
 * ------------------
 * Turns a Nova AI data-change action into a refresh of whatever is on screen —
 * and nothing else. Nova reports *what* changed ("tasks_updated",
 * "repair_updated", "status_updated") and, where it knows, *which* repair; the
 * app decides whether the screen in front of the technician shows that record.
 *
 *   tasks_updated / repair_updated
 *     - a repair is open → refetch it, but only if it is the changed repair
 *       (the same signal the Save button fires: repair.refetchData)
 *     - no repair open → the technician is on a list; bump the reload signal of
 *       the list that is showing (Home's repairs section, or the Repairs page),
 *       carrying the repair id so the list can skip a refetch for a repair it
 *       does not display
 *   status_updated
 *     - re-read the technician's status indicator so the header chip catches up
 *       (indirect / training activity changes it, and only the dashboard
 *       fetched it before)
 *   parts_searched
 *     - Nova searched the parts: put the same term in the Parts tab's own
 *       search box, so the rows are where they are requested from
 *   part_requested
 *     - Nova submitted a part request: open the Parts Request History popup,
 *       where it is now listed, when the open repair is the one it was for
 *   note_opened
 *     - Nova read one note aloud and this is that note: open its View popup,
 *       when the open repair is the one Nova named
 *   task_edit_discarded
 *     - Nova dropped the unsaved edit on a task card ("discard the change to
 *       task 1"); close that card the way Cancel does, when the open repair is
 *       the one it named
 *   task_edit_staged
 *     - Nova staged an edit on a task ("mark task 1 as Completed", "add a
 *       comment to task 1: …", "remove the comment from task 1"); open that
 *       card with the draft in it, unsaved, when the open repair is the one it
 *       named — the technician sees what "save task 1" will write
 */

import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '@shared/store';
import {setRefetchData} from '@shared/store/repairSlice';
import {
  triggerHomeReload,
  triggerNoteOpen,
  triggerPartsHistory,
  triggerPartsSearch,
  triggerRepairsReload,
  triggerTaskEditDiscard,
  triggerTaskEditStage,
  triggerConfiguredQty,
  triggerConfiguredSelect,
  triggerConfiguredSubmitted,
} from '@shared/store/novaAISlice';
import {setShiftStatusIndicator} from '@shared/store/shiftManagementSlice';
import {menuTabs} from '@features/layout/screens/MainLayoutScreen/constants/mainLayout.constant';
import {getShiftStatus} from '@features/dashboard/screens/DashboardScreen/DashboardController';

export type NovaDataActionType =
  | 'tasks_updated'
  | 'repair_updated'
  | 'status_updated'
  | 'task_edit_discarded'
  | 'task_edit_staged'
  | 'note_opened'
  | 'part_requested'
  | 'parts_searched'
  | 'configured_qty_set'
  | 'configured_part_selected'
  | 'configured_request_submitted';

export const useNovaDataRefresh = () => {
  const dispatch = useDispatch();
  const openRepairId = useSelector((state: RootState) => state.repair.selectedRepairId);
  const activeTab = useSelector((state: RootState) => state.menu.activeTab);
  const technicianId = useSelector(
    (state: RootState) => state.technician.selectedTechnician.selectedTechnicianId,
  );

  const refreshStatusIndicator = useCallback(async () => {
    const techId = Number(technicianId);
    if (!techId) return;
    try {
      const status = await getShiftStatus(techId);
      if (status?.statusValue !== undefined) {
        dispatch(setShiftStatusIndicator(status.statusValue));
      }
    } catch {
      // The chip keeps its last value; the next dashboard load corrects it.
    }
  }, [dispatch, technicianId]);

  const applyDataAction = useCallback(
    (
      actionType?: string,
      actionData?: {
        repairId?: string | number;
        stepNumber?: number;
        noteId?: string | number;
        searchText?: string;
        // Sent with part_requested; the hook does not read it, but a caller
        // passing the whole action data should type-check.
        partId?: string | number;
        requestedQty?: string | number;
        // The Configured Part List panel: which row, and for a quantity change
        // what to put in its box.
        qty?: string | number;
        // Sent with task_edit_staged: the draft to show on the task card.
        comment?: string;
        resultId?: number;
        resultName?: string;
      },
    ) => {
      if (actionType === 'status_updated') {
        void refreshStatusIndicator();
        return;
      }
      if (actionType === 'parts_searched') {
        const term = String(actionData?.searchText ?? '');
        const searchRepairId = String(actionData?.repairId ?? '');
        if (
          term &&
          openRepairId &&
          (!searchRepairId || searchRepairId === String(openRepairId))
        ) {
          dispatch(triggerPartsSearch(term));
        }
        return;
      }
      if (actionType === 'part_requested') {
        const requestRepairId = String(actionData?.repairId ?? '');
        if (
          openRepairId &&
          (!requestRepairId || requestRepairId === String(openRepairId))
        ) {
          dispatch(triggerPartsHistory());
        }
        return;
      }
      // The three below all name a row on the Configured Part List, which only
      // exists inside an open repair — so each is ignored unless the repair
      // Nova acted on is the one on screen. Same guard as parts_searched: Nova
      // can be asked about another repair by number, and ticking a box on a
      // panel the technician is not looking at would be worse than doing
      // nothing.
      if (actionType === 'configured_qty_set') {
        const partId = String(actionData?.partId ?? '');
        const qty = String(actionData?.qty ?? '');
        const qtyRepairId = String(actionData?.repairId ?? '');
        if (
          partId &&
          qty &&
          openRepairId &&
          (!qtyRepairId || qtyRepairId === String(openRepairId))
        ) {
          dispatch(triggerConfiguredQty({partId, qty}));
        }
        return;
      }
      if (actionType === 'configured_part_selected') {
        const partId = String(actionData?.partId ?? '');
        const selectRepairId = String(actionData?.repairId ?? '');
        if (
          partId &&
          openRepairId &&
          (!selectRepairId || selectRepairId === String(openRepairId))
        ) {
          dispatch(triggerConfiguredSelect(partId));
        }
        return;
      }
      // Nova has already posted the request; the panel clears its ticks and
      // opens the history. It does not submit — one request, one confirmation,
      // and that confirmation happened in the chat.
      if (actionType === 'configured_request_submitted') {
        const submitRepairId = String(actionData?.repairId ?? '');
        if (
          openRepairId &&
          (!submitRepairId || submitRepairId === String(openRepairId))
        ) {
          dispatch(triggerConfiguredSubmitted());
        }
        return;
      }
      if (actionType === 'note_opened') {
        const noteId = String(actionData?.noteId ?? '');
        const noteRepairId = String(actionData?.repairId ?? '');
        // Only the repair on screen: Nova can read a note on another repair
        // (asked by number), and opening a popup over a different repair's
        // notes would be wrong.
        if (
          noteId &&
          openRepairId &&
          (!noteRepairId || noteRepairId === String(openRepairId))
        ) {
          dispatch(triggerNoteOpen(noteId));
        }
        return;
      }
      if (actionType === 'task_edit_staged') {
        const stagedRepairId = String(actionData?.repairId ?? '');
        const stepNumber = Number(actionData?.stepNumber);
        if (
          stepNumber &&
          openRepairId &&
          (!stagedRepairId || stagedRepairId === String(openRepairId))
        ) {
          dispatch(
            triggerTaskEditStage({
              stepNumber,
              comment: actionData?.comment,
              resultId: actionData?.resultId,
              resultName: actionData?.resultName,
            }),
          );
        }
        return;
      }
      if (actionType === 'task_edit_discarded') {
        const discardedRepairId = String(actionData?.repairId ?? '');
        if (
          openRepairId &&
          (!discardedRepairId || discardedRepairId === String(openRepairId))
        ) {
          dispatch(triggerTaskEditDiscard(actionData?.stepNumber));
        }
        return;
      }
      if (actionType !== 'tasks_updated' && actionType !== 'repair_updated') {
        return;
      }
      const updatedRepairId = String(actionData?.repairId ?? '');

      if (openRepairId) {
        // A repair detail is on screen: refetch it only if it is the one that changed.
        if (updatedRepairId && updatedRepairId === String(openRepairId)) {
          dispatch(setRefetchData(true));
        }
        return;
      }

      // No repair open — a list is on screen. Refresh only the list that is.
      if (activeTab === menuTabs.home) {
        dispatch(triggerHomeReload());
      } else if (activeTab === menuTabs.repairs) {
        dispatch(triggerRepairsReload(updatedRepairId || undefined));
      }
    },
    [dispatch, openRepairId, activeTab, refreshStatusIndicator],
  );

  return {applyDataAction, refreshStatusIndicator};
};